import type {
  EventResponse,
  EventsListResponse,
  MarketsQueryParams,
  PaginatedResponse,
} from '@/types';
import { type CategoryType } from '@/types';
import axiosClient from './axiosClient';

import { API_ENDPOINTS } from '@/constants';

export interface CryptoFinanceFilter {
  id: string;
  label: string;
  count: number;
  icon?: string;
  tag_id?: string | null;
}

export interface CryptoFinanceSidebarResponse {
  success: boolean;
  data: CryptoFinanceFilter[];
  meta: {
    slug: string;
    description: string;
  };
}

export const getCryptoFinanceSidebar = async (
  category: CategoryType,
): Promise<CryptoFinanceSidebarResponse> => {
  const response = await axiosClient.get<CryptoFinanceSidebarResponse>(
    API_ENDPOINTS.CRYPTO_FINANCE_FILTERS(category),
  );
  return response.data;
};

/**
 * getCryptoEvents: Fetches paginated event data from the dedicated /markets/events/crypto endpoint.
 * This endpoint is specialized for the Crypto view and supports specific range/status filtering.
 */
export const getCryptoEvents = async (
  params?: MarketsQueryParams,
): Promise<PaginatedResponse<EventResponse>> => {
  const limit = params?.limit || 20;
  const offset = params?.offset !== undefined ? params.offset : ((params?.page || 1) - 1) * limit;
  const page = Math.floor(offset / limit) + 1;

  try {
    const { sortBy, status: _status, ...restParams } = params || {};
    const response = await axiosClient.get<EventsListResponse>(
      API_ENDPOINTS.MARKETS_EVENTS_CRYPTO,
      {
        params: {
          limit,
          offset,
          ...restParams,
          ascending: false,
          active: true,
          closed: false,
          order: 'volume24hr',
        },
      },
    );

    if (response && response.data?.success && Array.isArray(response.data?.data)) {
      const events = response.data.data;
      const total = events.length;
      const hasNextPage = events.length >= limit;

      return {
        items: events,
        total: total,
        page,
        limit,
        totalPages: hasNextPage ? page + 1 : page,
        hasNextPage: hasNextPage,
        hasPrevPage: page > 1,
      };
    }

    throw new Error('Invalid API response');
  } catch (e) {
    console.error('Crypto events API fetch failed:', e);
    throw e;
  }
};

export const cryptoFinanceApi = {
  getSidebar: getCryptoFinanceSidebar,
  getCryptoEvents,
};
